import React, { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { IconUser } from '../../../utils/icons';
import { NoDataView } from '../../../components/EmptyPanel/NoDataView';
import { IProduct } from '../../../components/Forms/Product/constants';
import { IEmbed } from '../../admin/embeds/embedsV2/interface';
import { SingleProduct } from './SingleProduct';
import axiosInstant from '../../../api';
import { deleteProductForBrand } from '../../../api/endpoints';
import { ConfirmationDialog } from '../../../components/ConfirmationDialog/ConfirmationDialog';

interface ProductsListProps {
  products: IProduct[];
  refetch: () => void;
}

export const ProductsList: FC<ProductsListProps> = ({ products, refetch }) => {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [deleteProduct, setDeleteProduct] = useState<IProduct>();

  const removeProduct = async () => {
    if (!id || !deleteProduct?.desireid) return;
    setLoading(true);
    try {
      await axiosInstant.delete(
        deleteProductForBrand(Number(id), Number(deleteProduct.desireid))
      );
      toast.success('Product association removed successfully');
      setDeleteProduct(undefined);
      refetch();
    } catch (err) {
      toast.error('Failed to remove product association');
    } finally {
      setLoading(false);
    }
  };

  const countStories = (product: IProduct) => {
    if (!product.productVideos) return 0;

    let total = 0;
    let stories: IEmbed[] = [];
    for (let i = 0; i < product.productVideos.length; i++) {
      if (product.productVideos[i].embeds) {
        stories = stories.concat(product.productVideos[i].embeds || []);
      }
    }
    stories = [
      ...new Map(stories.map((item: IEmbed) => [item.id, item])).values(),
    ];
    total = stories.length;
    return total;
  };

  return (
    <>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <span className="text-text-1">{products.length} Products</span>
        </div>
        <hr className="flex-grow border-t border-border-token" />
      </div>

      {products.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {products.map((product, i) => (
            <SingleProduct
              key={product.desireid}
              product={product}
              brandId={id}
              totalStories={countStories(product)}
              totalVideos={product.productVideos?.length}
              onDelete={() => setDeleteProduct(product)}
            />
          ))}
        </div>
      ) : (
        <NoDataView
          showTextInline
          Icon={IconUser}
          subText="Products will be visible once you have projects and videos in your account."
        />
      )}

      {deleteProduct && (
        <ConfirmationDialog
          open={!!deleteProduct?.desireid}
          onClose={() => setDeleteProduct(undefined)}
          onConfirm={removeProduct}
          confirmLabel="Remove"
          cancelLabel="Cancel"
          disabled={loading}
          title="Remove Product"
          description={`Are you sure you want to remove the association between ${deleteProduct?.title} and this Brand? This will not delete the product.`}
        />
      )}
    </>
  );
};
